/**
 * Everything the dashboard keeps in the browser that belongs to ONE identity,
 * and the three moments it is cleaned up.
 *
 * What is identity data (erased):
 * - decoded transaction bodies in IndexedDB (`./txCache`);
 * - offline copies of case workspaces — notes and comments — in IndexedDB
 *   (`@authe/collaboration`'s y-indexeddb persistence);
 * - the persisted signed-CDN decision, which names the tenant's private key
 *   paths (`./cdn`);
 * - recent searches (typed addresses and hashes) and an unsaved new-address
 *   draft in localStorage.
 *
 * What is NOT (kept): preferences — columns, density, theme, collapsed panels.
 * They are keyed per identity already, hold nothing a stranger could use, and
 * wiping them would make every sign-in start from scratch.
 *
 * The three moments:
 * - {@link clearIdentityBrowserData}`("logout")` — everything this person
 *   leaves behind, in every workspace they visited;
 * - {@link clearIdentityBrowserData}`("tenant-switch")` — the workspace being
 *   left; the person's preferences follow them;
 * - {@link pruneBrowserData} — once a session is established, for what no
 *   sign-out ever erased: a browser closed instead of signed out, a session that
 *   lapsed, another person's leftovers, cached bodies past their age.
 *
 * All of it is best-effort. A step that fails (IndexedDB blocked, storage
 * disabled) must not stop a sign-out, and every read path still refuses data
 * it may not use (keys are identity-scoped; bodies expire on read).
 */
import { pruneCollabPersistence } from "@authe/collaboration";
import { createScopedStorage, invalidateCdnSession, type StorageIdentity } from "@authe/fe-commons/storage";
import { SEARCH_RECENTS_NAME } from "@authe/search";
import { TenantContext } from "@authe/fe-commons/tenancy";
import { NEW_ADDRESS_DRAFT_KEY } from "@/features/addresses/newAddressDraft";
import { currentIdentity, txCache } from "./txCache";

/** localStorage values that are a person's DATA, not their preferences. */
const USER_DATA_KEYS: readonly string[] = [SEARCH_RECENTS_NAME, NEW_ADDRESS_DRAFT_KEY];

async function quietly(step: () => Promise<unknown> | unknown): Promise<void> {
  try {
    await step();
  } catch {
    /* Best-effort — see the module doc. */
  }
}

/** The room id's `{org}-{tenant}` segment, spelled the way collaboration spells it. */
function orgIdTenantId(identity: StorageIdentity): string {
  return new TenantContext(identity).orgIdTenantId;
}

/** Erase the identity data an identity change makes off-limits. */
export async function clearIdentityBrowserData(reason: "logout" | "tenant-switch"): Promise<void> {
  const identity = currentIdentity();
  const cache = txCache();
  await Promise.all([
    // A sign-out erases every tenant's bodies; a switch only the tenant left.
    quietly(() => (reason === "logout" ? cache.clearAll() : cache.clear("tenant"))),
    quietly(() =>
      pruneCollabPersistence((owner) =>
        reason === "logout"
          ? owner.userId !== identity.userId
          : owner.userId !== identity.userId || owner.orgIdTenantId !== orgIdTenantId(identity),
      ),
    ),
    // Memory AND the persisted copy: `clearCdnSession` only drops the former.
    quietly(() => invalidateCdnSession()),
    quietly(() => {
      if (reason !== "logout" || !identity.userId) return;
      const storage = createScopedStorage(identity);
      for (const name of USER_DATA_KEYS) storage.remove(name, "user");
    }),
  ]);
}

/**
 * Keep only the signed-in identity's data: other people's offline copies and
 * cached bodies go, and so do this identity's bodies past their age.
 *
 * Another person's case copies are pruned; this person's copies in their other
 * workspaces are kept — they may hold offline edits not yet synced.
 */
export async function pruneBrowserData(): Promise<void> {
  const identity = currentIdentity();
  if (!identity.userId) return;
  await Promise.all([
    quietly(() => txCache().prune()),
    quietly(() => pruneCollabPersistence((owner) => owner.userId === identity.userId)),
  ]);
}
